// Document lineage resolution for the exclusion bake (荒らし復旧 第 2 段階 ①,
// vandalism-recovery-ui-2026-09-27.md §3「範囲 = 現行文書の系譜」).
//
// Shared by the server bake (`ExclusionBakeService`) and the web exclusion
// preview, which decide G (the base generation) from the same plan. Web
// `lineageSeed.ts` (`collectFinalDocumentRanges`) is a separate implementation
// of the same rule that only needs seq ranges — KEEP IN LOCKSTEP with it:
//
//   - a **document** starts at a full `whiteboard.clear` (layerId omitted), a
//     `whiteboard.restore` marker, or the head of the log;
//   - a document that starts with a restore marker `m` continues the source
//     document up to `restoredFromSeq = q` — the events `(q, m]` of the source
//     are the **abandoned branch** and never replayed (never merged);
//   - the lineage is therefore `(start, q1] ∪ (m1, q2] ∪ … ∪ (m_k, end]`, root →
//     leaf.
//
// Unlike the web (which only needs seq ranges), the bake also needs the
// **sessionId of every document** so it can read that document's snapshot
// generations. The leaf's session is given; an earlier document's session is the
// marker's `restoredFromSessionId`.
//
// A restore whose source is itself a baked "excluded" row (`kind: 'excluded'`)
// ends the walk: the baked session has no stroke log of its own (its pixels are
// synthetic), so that session becomes a `baked` root whose only usable base is
// its single generation `snapshot-{q}`.
/** Same depth cap as web `MAX_LINEAGE_DEPTH` — beyond it the root is dropped. */
export const MAX_LINEAGE_DEPTH = 10;
const isFullClear = (ev) => ev.t === 'whiteboard.clear' && ev.layerId === undefined;
/** A document boundary event: a full clear or a restore marker. */
export const isDocumentBoundary = (ev) => isFullClear(ev) || ev.t === 'whiteboard.restore';
/**
 * A past-canvas row's `clearedSeq` is the room's seq *at* the clear / restore
 * that closed the document, i.e. usually the boundary event itself. The document
 * ends one before it (otherwise the bake would end with a wipe).
 */
export const normalizeEndSeq = (events, endSeq) => {
    for (let i = events.length - 1; i >= 0; i--) {
        const ev = events[i];
        if (ev === undefined)
            continue;
        if (ev.seq > endSeq)
            continue;
        if (ev.seq < endSeq)
            return endSeq;
        return isDocumentBoundary(ev) ? endSeq - 1 : endSeq;
    }
    return endSeq;
};
/** Last document boundary at or before `maxSeq`; `head` = first event; null = no events. */
const findBoundary = (events, maxSeq) => {
    let head = null;
    let hit = null;
    for (const ev of events) {
        if (ev.seq > maxSeq)
            break;
        if (head === null)
            head = ev.seq;
        if (ev.t === 'whiteboard.clear' && ev.layerId === undefined) {
            hit = { kind: 'clear', seq: ev.seq };
        }
        else if (ev.t === 'whiteboard.restore') {
            hit = { kind: 'restore', marker: ev };
        }
    }
    if (hit !== null)
        return hit;
    return head !== null ? { kind: 'head', seq: head } : null;
};
export const resolveLineage = (input) => {
    const { events, bakedSessionIds, hasEarlierSegments } = input;
    const maxDepth = input.maxDepth ?? MAX_LINEAGE_DEPTH;
    const docs = []; // built leaf → root, reversed at the end
    let sessionId = input.leafSessionId;
    let to = input.endSeq;
    for (let depth = 0;; depth++) {
        const boundary = findBoundary(events, to);
        if (boundary === null) {
            // Nothing loaded at or below `to`.
            if (hasEarlierSegments)
                return { kind: 'need-earlier', belowSeq: to };
            docs.push({ sessionId, origin: 'head', eventsFrom: to + 1, eventsTo: to, candidateMin: to });
            break;
        }
        if (boundary.kind === 'restore') {
            const m = boundary.marker;
            docs.push({
                sessionId,
                origin: 'restore',
                eventsFrom: m.seq + 1,
                eventsTo: to,
                candidateMin: m.seq,
            });
            const q = m.restoredFromSeq;
            if (q >= m.seq)
                break; // malformed marker: stop at this document
            if (depth + 1 >= maxDepth)
                break; // degrade: root dropped (caller needs a base gen)
            if (bakedSessionIds.has(m.restoredFromSessionId)) {
                docs.push({
                    sessionId: m.restoredFromSessionId,
                    origin: 'baked',
                    eventsFrom: q + 1,
                    eventsTo: q,
                    candidateMin: q,
                });
                break;
            }
            sessionId = m.restoredFromSessionId;
            to = q;
            continue;
        }
        if (boundary.kind === 'head' && hasEarlierSegments) {
            // The "head" is just where our loaded window starts — keep looking.
            return { kind: 'need-earlier', belowSeq: boundary.seq };
        }
        docs.push({
            sessionId,
            origin: boundary.kind,
            eventsFrom: boundary.seq,
            eventsTo: to,
            candidateMin: boundary.seq,
        });
        break;
    }
    docs.reverse();
    return { kind: 'ok', docs };
};
//# sourceMappingURL=lineage.js.map